'use client';

import { useCallback } from 'react';

import type {
  CheckoutSessionResponse,
  PlanChangePreview,
  RecurringConsentInput,
  SubscriptionInfo,
} from '@/types/billing';
import { useToast } from '@/hooks/use-toast';

interface CheckoutOptions {
  plan: 'hobby' | 'basic' | 'pro';
  mode?: 'subscription' | 'setup' | 'recovery';
  recovery_invoice_id?: string;
  success_url?: string;
  cancel_url?: string;
  recurring_consent?: RecurringConsentInput;
}

interface PlanChangeOptions {
  effectiveImmediately?: boolean;
}

async function extractError(response: Response): Promise<string> {
  const fallback = response.statusText || 'Запрос завершился ошибкой';
  try {
    const data = (await response.json()) as { error?: string; detail?: string };
    return data.error || data.detail || fallback;
  } catch {
    return fallback;
  }
}

export function useBillingActions() {
  const { toast } = useToast();

  const startCheckout = useCallback(
    async (options: CheckoutOptions) => {
      try {
        const response = await fetch('/api/billing/checkout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(options),
        });

        if (!response.ok) {
          throw new Error(await extractError(response));
        }

        const payload = (await response.json()) as CheckoutSessionResponse;
        if (!payload?.url) {
          throw new Error('Сервис оплаты не вернул ссылку для перехода');
        }

        window.location.assign(payload.url);
      } catch (error) {
        console.error('Failed to start checkout session', error);
        toast({
          variant: 'destructive',
          title: 'Не удалось открыть оплату',
          description:
            error instanceof Error ? error.message : 'Не удалось запустить переход к оплате.',
        });
        throw error;
      }
    },
    [toast],
  );

  const previewPlanChange = useCallback(
    async (plan: 'basic' | 'pro', options?: PlanChangeOptions) => {
      try {
        const payload: Record<string, unknown> = { plan };
        if (options?.effectiveImmediately !== undefined) {
          payload.effective_immediately = options.effectiveImmediately;
        }
        const response = await fetch('/api/billing/change-plan/preview', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(await extractError(response));
        }

        return (await response.json()) as PlanChangePreview;
      } catch (error) {
        console.error('Failed to preview subscription plan change', error);
        toast({
          variant: 'destructive',
          title: 'Не удалось рассчитать смену тарифа',
          description:
            error instanceof Error ? error.message : 'Не удалось получить данные по смене тарифа.',
        });
        throw error;
      }
    },
    [toast],
  );

  const changePlan = useCallback(
    async (plan: 'basic' | 'pro', options?: PlanChangeOptions) => {
      try {
        const payload: Record<string, unknown> = { plan };
        if (options?.effectiveImmediately !== undefined) {
          payload.effective_immediately = options.effectiveImmediately;
        }
        const response = await fetch('/api/billing/change-plan', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(await extractError(response));
        }

        return (await response.json()) as SubscriptionInfo;
      } catch (error) {
        console.error('Failed to change subscription plan', error);
        toast({
          variant: 'destructive',
          title: 'Не удалось сменить тариф',
          description:
            error instanceof Error ? error.message : 'Не удалось обновить параметры подписки.',
        });
        throw error;
      }
    },
    [toast],
  );

  const cancelPendingPlanChange = useCallback(async () => {
    try {
      const response = await fetch('/api/billing/pending-plan/cancel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(await extractError(response));
      }

      return (await response.json()) as SubscriptionInfo;
    } catch (error) {
      console.error('Failed to cancel pending plan change', error);
      toast({
        variant: 'destructive',
        title: 'Не удалось отменить запланированную смену тарифа',
        description:
          error instanceof Error ? error.message : 'Не удалось обновить параметры подписки.',
      });
      throw error;
    }
  }, [toast]);

  return {
    startCheckout,
    previewPlanChange,
    changePlan,
    cancelPendingPlanChange,
  };
}
